// PwaInstall — Service Worker register + "Ana Ekrana Ekle" banner'ı.
// Kullanım: App.js içinde bir kere <PwaInstall /> render et.
//   - SW'i mount'ta register eder
//   - beforeinstallprompt event'ini yakalar
//   - 3 ziyaretten sonra üstte küçük banner gösterir
//   - Kullanıcı dismiss'lerse 30 gün hatırlamaz
import React, { useEffect, useState, useCallback } from 'react';

const VISIT_COUNT_KEY = 'hent.pwa.visits';
const DISMISS_KEY = 'hent.pwa.dismissed_at';
const DISMISS_DAYS = 30;

export default function PwaInstall() {
  const [installEvent, setInstallEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Visit count + dismiss kontrolü
  useEffect(() => {
    try {
      const n = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0') + 1;
      localStorage.setItem(VISIT_COUNT_KEY, String(n));
      const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0');
      const dismissValid = dismissedAt && (Date.now() - dismissedAt) < DISMISS_DAYS * 86400000;
      if (n >= 3 && !dismissValid) {
        // installEvent geldiğinde göstereceğiz
      }
    } catch {}
  }, []);

  // SW register
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    if (process.env.NODE_ENV !== 'production' && !window.location.hostname.includes('asistanyazilim')) return;
    navigator.serviceWorker.register('/service-worker.js').then((reg) => {
      setRegistration(reg);
      // Update check
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) {
            setUpdateReady(true);
          }
        });
      });
    }).catch((err) => {
      console.warn('SW register fail:', err.message);
    });
  }, []);

  // beforeinstallprompt — Chrome/Edge/Samsung Internet
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      try {
        const n = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0');
        const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0');
        const dismissValid = dismissedAt && (Date.now() - dismissedAt) < DISMISS_DAYS * 86400000;
        if (n >= 3 && !dismissValid) setShow(true);
      } catch {}
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // App installed → temizle
  useEffect(() => {
    const handler = () => { setShow(false); setInstallEvent(null); };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const onInstall = useCallback(async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice.catch(() => null);
    if (choice?.outcome === 'accepted') {
      setShow(false);
    }
    setInstallEvent(null);
  }, [installEvent]);

  const onDismiss = useCallback(() => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  }, []);

  const onApplyUpdate = useCallback(() => {
    if (!registration?.waiting) {
      window.location.reload();
      return;
    }
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    setTimeout(() => window.location.reload(), 200);
  }, [registration]);

  return (
    <>
      {show && installEvent && (
        <div style={S.banner}>
          <span style={S.icon}>📲</span>
          <div style={S.text}>
            <strong>hent'i ana ekrana ekle</strong>
            <span style={S.sub}>Daha hızlı erişim, çevrimdışı çalışma, native bildirim</span>
          </div>
          <button onClick={onInstall} style={S.installBtn}>Yükle</button>
          <button onClick={onDismiss} style={S.dismissBtn} aria-label="Kapat">×</button>
        </div>
      )}
      {updateReady && (
        <div style={{ ...S.banner, ...S.updateBanner }}>
          <span style={S.icon}>🔄</span>
          <div style={S.text}>
            <strong>Yeni sürüm hazır</strong>
            <span style={S.sub}>Güncellemek için yenileyin</span>
          </div>
          <button onClick={onApplyUpdate} style={S.installBtn}>Yenile</button>
          <button onClick={() => setUpdateReady(false)} style={S.dismissBtn} aria-label="Kapat">×</button>
        </div>
      )}
    </>
  );
}

const S = {
  banner: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    right: 16,
    maxWidth: 480,
    margin: '0 auto',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderLeft: '4px solid var(--accent-primary)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-lg)',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    zIndex: 9500,
    animation: 'pwa-slide-up 0.2s ease-out',
  },
  updateBanner: {
    borderLeftColor: 'var(--success)',
  },
  icon: { fontSize: 22 },
  text: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  sub: { fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 },
  installBtn: {
    padding: '6px 14px',
    background: 'var(--accent-primary)',
    color: 'var(--text-on-accent)',
    border: 'none',
    borderRadius: 'var(--r-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 'var(--text-sm)',
  },
  dismissBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    padding: '0 4px',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('pwa-keyframes')) {
  const s = document.createElement('style');
  s.id = 'pwa-keyframes';
  s.textContent = '@keyframes pwa-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }';
  document.head.appendChild(s);
}
